import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useRazorpay from 'react-razorpay';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import { getCourses, createCoursePaymentOrder, verifyPayment, enrollInCourse } from '../services/secureApi';
import { useAuth } from '../hooks/useAuth';
import SessionManager from '../utils/sessionManager';

// Add animation styles
const modalStyles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  .animate-fade-in {
    animation: fadeIn 0.3s ease-out;
  }
`;

const CourseBillingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, role } = useAuth();
  const [Razorpay] = useRazorpay();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  
  // Billing form state
  const [billingInfo, setBillingInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    console.log('🔄 CourseBillingPage useEffect triggered, id:', id);
    
    // Check authentication using SessionManager directly
    const candidateSession = SessionManager.getSession('candidate');
    const hasValidSession = candidateSession && candidateSession.access_token;
    
    console.log('🔐 CourseBillingPage auth check:', {
      hasValidSession: !!hasValidSession,
      candidateSessionExists: !!candidateSession,
      isLoggedInHook: isLoggedIn(),
      roleFromHook: role
    });
    
    if (!hasValidSession) {
      console.log('❌ No valid candidate session, redirecting to login');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    console.log('✅ Auth check passed, loading course data');
    loadCourseData();
  }, [id]); // Only re-run when id changes

  const loadCourseData = async () => {
    console.log('📚 Loading course data for ID:', id);
    try {
      const courses = await getCourses();
      console.log('📚 Courses loaded:', courses.length, 'courses');
      
      // Compare as strings since course IDs can be like "course-1772002384061"
      const foundCourse = courses.find(c => String(c.id) === String(id));
      console.log('📚 Looking for course ID:', id);
      console.log('📚 Found course:', foundCourse ? foundCourse.title : 'NOT FOUND');
      
      if (foundCourse) {
        setCourse(foundCourse);
        console.log('✅ Course set successfully');
      } else {
        console.log('❌ Course not found, redirecting to /courses');
        console.log('Available course IDs:', courses.map(c => c.id).join(', '));
        navigate('/courses');
      }
    } catch (error) {
      console.error('❌ Error loading course:', error);
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!billingInfo.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!billingInfo.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(billingInfo.email)) newErrors.email = 'Email is invalid';
    if (!billingInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(billingInfo.phone.replace(/\D/g, ''))) newErrors.phone = 'Phone number must be 10 digits';
    if (!billingInfo.address.trim()) newErrors.address = 'Address is required';
    if (!billingInfo.city.trim()) newErrors.city = 'City is required';
    if (!billingInfo.state.trim()) newErrors.state = 'State is required';
    if (!billingInfo.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(billingInfo.pincode)) newErrors.pincode = 'Pincode must be 6 digits';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const applyCoupon = () => {
    // Simple coupon logic - you can enhance this
    if (couponCode.toUpperCase() === 'SAVE10') {
      setDiscount(10);
      setCouponApplied(true);
    } else if (couponCode.toUpperCase() === 'SAVE20') {
      setDiscount(20);
      setCouponApplied(true);
    } else {
      alert('Invalid coupon code');
      setDiscount(0);
      setCouponApplied(false);
    }
  };

  const calculateTotal = () => {
    if (!course) return 0;
    const subtotal = course.price;
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      alert('Please fill in all required fields correctly');
      return;
    }
    
    // Re-check session before payment
    const candidateSession = SessionManager.getSession('candidate');
    const currentCandidateId = candidateSession?.user_id;
    const currentToken = candidateSession?.access_token;
    
    console.log('🔐 CourseBillingPage payment auth check:', {
      hasCandidateId: !!currentCandidateId,
      hasToken: !!currentToken
    });
    
    if (!currentCandidateId || !currentToken) {
      console.log('❌ Session expired, redirecting to login');
      alert('Your session has expired. Please login to continue');
      navigate('/login');
      return;
    }

    setPaymentLoading(true);

    try {
      const amount = calculateTotal();
      
      // Create payment order with proper payload structure matching backend expectations
      const orderResponse = await createCoursePaymentOrder({
        course_id: course.id,
        course_amount: amount,  // Backend expects 'course_amount', not 'amount'
        candidate_id: currentCandidateId,
        billing_info: billingInfo
      });
      
      console.log('💳 Payment order created:', orderResponse);
      
      if (!orderResponse.order_id) {
        throw new Error('Failed to create payment order');
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_S9Mi0lChtnAtHt',
        amount: orderResponse.amount,
        currency: orderResponse.currency,
        name: 'Aptirix',
        description: course.title,
        order_id: orderResponse.order_id,
        prefill: {
          name: billingInfo.fullName,
          email: billingInfo.email,
          contact: billingInfo.phone,
        },
        notes: {
          course_id: course.id,
          candidate_id: currentCandidateId,
          billing_address: `${billingInfo.address}, ${billingInfo.city}, ${billingInfo.state} - ${billingInfo.pincode}`,
        },
        theme: {
          color: '#0a1929',
        },
        handler: async function (response) {
          try {
            console.log('✅ [PAYMENT] Payment successful, verifying...');
            console.log('Payment response:', response);
            
            const verifyResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            console.log('✅ [PAYMENT] Verification response:', verifyResponse);

            // Check for success (backend returns { success: true })
            if (verifyResponse.success === true) {
              console.log('✅ [PAYMENT] Payment verified successfully');
              
              // Enroll in course (may already be done by backend, but ensure it)
              try {
                await enrollInCourse(course.id, currentToken);
                console.log('✅ [PAYMENT] Course enrollment confirmed');
              } catch (enrollError) {
                console.log('⚠️ [PAYMENT] Enrollment API call failed (may already be enrolled):', enrollError);
              }
              
              // Calculate invoice amounts
              const invoiceSubtotal = course.price;
              const invoiceDiscount = (invoiceSubtotal * discount) / 100;
              const invoiceTotal = amount;
              
              // Try to generate invoice (backend may have already done this)
              try {
                console.log('📄 Generating invoice...');
                console.log('📄 Invoice data:', {
                  transaction_id: response.razorpay_payment_id,
                  payment_id: response.razorpay_payment_id,
                  order_id: response.razorpay_order_id,
                  course_id: course.id,
                  course_title: course.title,
                  course_price: invoiceSubtotal,
                  discount: invoiceDiscount,
                  total: invoiceTotal
                });
                
                const invoiceResponse = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL || 'https://aptirix-backend-469811621499.asia-south1.run.app'}/api/invoices/generate-course-invoice`,
                  {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${currentToken}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      transaction_id: response.razorpay_payment_id,
                      payment_id: response.razorpay_payment_id,
                      order_id: response.razorpay_order_id,
                      course_id: course.id,
                      course_title: course.title,
                      course_price: invoiceSubtotal,
                      discount: invoiceDiscount,
                      tax: 0,
                      total: invoiceTotal,
                      billing_info: billingInfo
                    })
                  }
                );
                
                console.log('📄 Invoice response status:', invoiceResponse.status);
                
                if (invoiceResponse.ok) {
                  const invoiceData = await invoiceResponse.json();
                  console.log('✅ Invoice generated:', invoiceData.invoice_number);
                  setInvoiceNumber(invoiceData.invoice_number);
                } else {
                  console.log('⚠️ Invoice generation failed, but payment was successful');
                  const errorText = await invoiceResponse.text();
                  console.error('Invoice error details:', errorText);
                }
              } catch (invoiceError) {
                console.error('Invoice generation error:', invoiceError);
              }
              
              // Show success modal
              setShowSuccessModal(true);
            } else {
              alert('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed. Please contact support.');
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setPaymentLoading(false);
          },
        },
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to initiate payment. Please try again.');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  const subtotal = course.price;
  const discountAmount = (subtotal * discount) / 100;
  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{modalStyles}</style>
      <LandingHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Course
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Side - Billing Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Checkout</h1>
              <p className="text-gray-600 mb-8">Complete your purchase to start learning</p>

              <form className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="fullName"
                        value={billingInfo.fullName}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className={errors.fullName ? 'border-red-500' : ''}
                      />
                      {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={billingInfo.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="phone"
                      value={billingInfo.phone}
                      onChange={handleInputChange}
                      placeholder="10-digit mobile number"
                      className={errors.phone ? 'border-red-500' : ''}
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                {/* Billing Address */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing Address</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="address"
                        value={billingInfo.address}
                        onChange={handleInputChange}
                        placeholder="Street address, apartment, suite, etc."
                        className={errors.address ? 'border-red-500' : ''}
                      />
                      {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="city"
                          value={billingInfo.city}
                          onChange={handleInputChange}
                          placeholder="City"
                          className={errors.city ? 'border-red-500' : ''}
                        />
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="state"
                          value={billingInfo.state}
                          onChange={handleInputChange}
                          placeholder="State"
                          className={errors.state ? 'border-red-500' : ''}
                        />
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pincode <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="pincode"
                          value={billingInfo.pincode}
                          onChange={handleInputChange}
                          placeholder="6-digit pincode"
                          className={errors.pincode ? 'border-red-500' : ''}
                        />
                        {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <Input
                          name="country"
                          value={billingInfo.country}
                          disabled
                          className="bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Right Side - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Order</h2>
              
              {/* Course Info */}
              <div className="mb-6">
                <div className="flex gap-4">
                  <img
                    src={course.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=200&h=120&fit=crop'}
                    alt={course.title}
                    className="w-24 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">{course.title}</h3>
                    <p className="text-blue-600 font-bold text-lg mt-1">₹{course.price.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              {/* Coupon Code */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply Coupon</label>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter code"
                    disabled={couponApplied}
                    className="flex-1"
                  />
                  <Button
                    onClick={applyCoupon}
                    disabled={couponApplied || !couponCode}
                    className="bg-[#0a1929] hover:bg-[#0d2136] text-white px-6"
                  >
                    Apply
                  </Button>
                </div>
                {couponApplied && (
                  <p className="text-green-600 text-sm mt-2">✓ Coupon applied successfully!</p>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-3 mb-6">
                <div className="flex justify-between items-center text-gray-700">
                  <span className="text-base">Subtotal</span>
                  <span className="text-base font-medium">₹{subtotal.toLocaleString('en-IN')}</span>
                </div>
                
                {discount > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span className="text-base">Discount ({discount}%)</span>
                    <span className="text-base font-medium">-₹{discountAmount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-gray-700">
                  <span className="text-base">Tax</span>
                  <span className="text-base font-medium">₹0.00</span>
                </div>
                
                <div className="border-t-2 border-gray-300 pt-4 flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-900">Total</span>
                  <span className="text-3xl font-bold text-blue-600">₹{total.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Payment Button */}
              <Button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="w-full bg-[#0a1929] hover:bg-[#0d2136] text-white py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paymentLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Pay with Razorpay
                  </>
                )}
              </Button>
              {/* Security Note */}
              <p className="text-xs text-gray-500 text-center mt-4">
                By completing your purchase, you agree to our{' '}
                <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
              </p>

              {/* Security Badges */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center gap-4 text-gray-400">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                  <span className="text-sm">Secure Payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            
            {/* Success Message */}
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-3">
              Payment Successful!
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Congratulations! You are now enrolled in <span className="font-semibold text-gray-900">{course?.title}</span>
            </p>
            
            {/* Invoice Info */}
            {invoiceNumber && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Invoice Generated</p>
                    <p className="text-sm text-blue-700">
                      Invoice <span className="font-mono font-semibold">{invoiceNumber}</span> has been sent to your email.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Course Access Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 mb-1">Course Access Granted</p>
                  <p className="text-sm text-green-700">
                    You can now access all course materials and start learning immediately.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/candidate/courses')}
                className="w-full bg-[#0a1929] hover:bg-[#0d2136] text-white py-3 text-base font-semibold rounded-xl"
              >
                Go to My Courses
              </Button>
              <button
                onClick={() => navigate('/')}
                className="w-full text-gray-600 hover:text-gray-900 py-2 text-sm font-medium transition"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default CourseBillingPage;
